import type { FC } from 'react';

import type { AlertStatus } from '@/types/alert-status';
import { cls } from '@/utils/cls';

export type AlertStatusBadgeProps = {
  status: AlertStatus;
};

const labelFromStatus: Record<AlertStatus, string> = {
  OK: 'OK',
  Info: 'Info',
  Warning: 'Warning',
  Tripped: 'Tripped',
  Critical: 'Critical',
};

const bgFromStatus: Record<AlertStatus, string> = {
  OK: 'bg-emerald-500/10',
  Info: 'bg-[#86DFC2]/30',
  Warning: 'bg-yellow-500/30',
  Tripped: 'bg-[#A97B50]/30', // brownish for tripped
  Critical: 'bg-red-500/30',
};

const dotFromStatus: Record<AlertStatus, string> = {
  OK: 'bg-emerald-500',
  Info: 'bg-[#86DFC2]',
  Warning: 'bg-yellow-500',
  Tripped: 'bg-[#A97B50]', // brownish for tripped
  Critical: 'bg-red-500',
};

// Slightly darker stroke so the slash is visible on the translucent bg
const strokeFromStatus: Partial<Record<AlertStatus, string>> = {
  Tripped: 'text-[#A97B50]', // brownish for tripped
};

export const StatusLabel: FC<AlertStatusBadgeProps> = ({ status }) => (
  <p className="text-xs leading-5 text-gray-500">{labelFromStatus[status]}</p>
);

export const TrippedStatusIcon: FC = () => (
  <div
    className={cls(
      'flex-none rounded-full p-1', // full circle chip
      bgFromStatus.Tripped,
    )}
    aria-label="Tripped"
  >
    <svg
      viewBox="0 0 12 12"
      className={cls('block size-2', strokeFromStatus.Tripped)}
    >
      {/* No rect needed; parent is fully rounded */}
      <path
        d="M3 9 L9 3"
        stroke="currentColor"
        strokeWidth="5"
        strokeLinecap="round"
      />
    </svg>
  </div>
);

export const StatusIcon: FC<AlertStatusBadgeProps> = ({ status }) => {
  if (status === 'Tripped') {
    return <TrippedStatusIcon />;
  }

  // Default: original dot-in-chip
  return (
    <div className={cls('flex-none rounded-full p-1', bgFromStatus[status])}>
      <div className={cls('size-1.5 rounded-full', dotFromStatus[status])} />
    </div>
  );
};

export const AlertStatusBadge: FC<AlertStatusBadgeProps> = ({ status }) => (
  <div className="mt-1 flex items-center gap-x-1.5">
    <StatusLabel status={status} />
    <StatusIcon status={status} />
  </div>
);
